import axios, { AxiosHeaders, isAxiosError } from 'axios';
import type { User } from 'firebase/auth';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';

import { auth } from 'lib/firebase';
import { notifyTermsGateRequired } from 'lib/termsGate';
import { ORGANIZATION_HEADER } from './organizationHeader';

const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL ?? '').replace(
  /\/$/,
  '',
);

axios.defaults.baseURL = apiBaseUrl;

/** 組織の再選択（/select-organization への誘導）が必要なエラー code */
export const ORGANIZATION_SELECTION_ERROR_CODES: ReadonlySet<string> = new Set([
  'ORGANIZATION_REQUIRED',
  'INVALID_ORGANIZATION_ID',
  'ORGANIZATION_ACCESS_DENIED',
  'NO_ACTIVE_MEMBERSHIP',
]);

export type ApiErrorHandler = {
  handleOrganizationSelectionError: (code: string) => void;
  handleForcedSignOut: (code: string | undefined) => void | Promise<void>;
};

let apiErrorHandler: ApiErrorHandler | null = null;
let isHandlingUnauthorized = false;

/** ApiErrorWatcher（Router 配下）が登録し、エラー code の一元処理を担う。 */
export function setApiErrorHandler(handler: ApiErrorHandler | null): void {
  apiErrorHandler = handler;
}

const waitForFirebaseUser = (): Promise<User | null> =>
  new Promise((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      () => {
        unsubscribe();
        resolve(null);
      },
    );
  });

/** 要求した利用者と、送信時の Firebase の利用者が違う（ログアウト・別アカウントへの切り替え） */
export class AuthSubjectChangedError extends Error {
  constructor() {
    super('auth subject changed');
    this.name = 'AuthSubjectChangedError';
  }
}

// アクセストークン付与
axios.interceptors.request.use(async (config) => {
  const currentUser = auth.currentUser ?? (await waitForFirebaseUser());
  if (config.expectedAuthUid && currentUser?.uid !== config.expectedAuthUid) {
    throw new AuthSubjectChangedError();
  }
  if (!currentUser) return config;

  const idToken = await currentUser.getIdToken();
  if (!idToken) return config;

  const headers = AxiosHeaders.from(config.headers);
  headers.set('Authorization', `Bearer ${idToken}`);
  config.headers = headers;
  // 応答が遅れて届いても、要求を出した本人の規約状態だけを止められるよう記録する（送信しない）
  config.authUid = currentUser.uid;

  return config;
});

function organizationHeaderValue(value: unknown): string | null {
  return value === undefined || value === null || value === ''
    ? null
    : String(value);
}

function extractErrorCode(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const code = (data as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAxiosError(error)) {
      const data = error.response?.data;
      // messageが配列の場合は先頭を採用する。
      const message =
        typeof data?.message === 'string'
          ? data.message
          : Array.isArray(data?.message) && typeof data.message[0] === 'string'
            ? data.message[0]
            : error.message;

      error.message = message;
      error.status = error.response?.status;

      const code = extractErrorCode(data);
      const status = error.response?.status;

      // 利用規約の同意が必要（またはfail-closed）。ログアウトせず画面側で同意画面へ案内する
      const authUid = error.config?.authUid;
      if (authUid && status === 403 && code === 'TERMS_ACCEPTANCE_REQUIRED') {
        notifyTermsGateRequired('TERMS_ACCEPTANCE_REQUIRED', authUid);
      } else if (authUid && status === 503 && code === 'TERMS_UNAVAILABLE') {
        notifyTermsGateRequired('TERMS_UNAVAILABLE', authUid);
      }
      const requestHeaders = AxiosHeaders.from(error.config?.headers);
      const requestHadAuthorization = Boolean(
        requestHeaders.get('Authorization'),
      );
      // 前の利用者・切り替え前の組織のリクエストが遅れて失敗しても、今の利用者の画面を動かさない
      const isCurrentAuthSubject =
        Boolean(authUid) && authUid === auth.currentUser?.uid;
      const isCurrentOrganization =
        organizationHeaderValue(requestHeaders.get(ORGANIZATION_HEADER)) ===
        organizationHeaderValue(
          axios.defaults.headers.common[ORGANIZATION_HEADER],
        );

      if (code && ORGANIZATION_SELECTION_ERROR_CODES.has(code)) {
        if (isCurrentAuthSubject && isCurrentOrganization) {
          apiErrorHandler?.handleOrganizationSelectionError(code);
        }
      } else if (
        isCurrentAuthSubject &&
        ((status === 401 && requestHadAuthorization) ||
          (status === 403 && code === 'ACCOUNT_PAUSED')) &&
        !isHandlingUnauthorized
      ) {
        isHandlingUnauthorized = true;
        try {
          if (apiErrorHandler) {
            await apiErrorHandler.handleForcedSignOut(code);
          } else if (auth.currentUser) {
            await firebaseSignOut(auth);
          }
        } catch {
          // noop
        } finally {
          isHandlingUnauthorized = false;
        }
      }
    }
    return Promise.reject(error);
  },
);

export { axios };
